import React, {useCallback} from 'react';
import {Form} from 'react-final-form';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import OrderModel from 'coreClasses/order/OrderModel';
import PWAException from 'coreClasses/PWAException';
import {useOrdersMgr} from 'coreHooks';
import validationRules from 'coreUtils/validationRules';

import Button from 'brandComponents/Button';
import Field from 'brandComponents/Form/Subcomponents/Field';
import Grid from 'brandComponents/Grid';
import Typography from 'brandComponents/Typography';
import VerifyForm from 'brandComponents/VerifyForm';

import {ITrackOrderFormProps} from './ITrackOrderForm';
import messages from './messages';

import styles from './TrackOrderForm.module.scss';

const TrackOrderForm = ({onOrderTracked}: ITrackOrderFormProps) => {
	const intl = useIntl();
	const ordersMgr = useOrdersMgr();
	const [orderError, setOrderError] = React.useState<string>(null);

	const handleSubmitForm = useCallback(async (values) => {
		const {orderNo, zip} = values;

		try {
			const trackOrder: OrderModel = await ordersMgr.getOrderByZipCode(orderNo, zip);

			if (trackOrder) {
				onOrderTracked(trackOrder);
			}
		} catch (e) {
			if (e instanceof PWAException) {
				setOrderError(e.message);
			} else {
				setOrderError(intl.formatMessage(messages.orderError));
			}
		}
	}, []);

	return (
		<div className={styles['track-order-form']}>
			<Grid fluid classNames={styles['container-fluid']}>
				<Grid.Row>
					<Grid.Column md={12}>
						<Typography classNames={styles['track-order-form__title']} variant="h2">
							{intl.formatMessage(messages.title)}
						</Typography>

						<Typography classNames={styles['track-order-form__description']} variant="p">
							{intl.formatMessage(messages.description)}
						</Typography>
					</Grid.Column>
				</Grid.Row>

				<Form
					onSubmit={(values) => handleSubmitForm(values)}
					render={({handleSubmit, submitting}) => (
						<VerifyForm classNames={styles['track-order-form__form']} onSubmit={handleSubmit}>
							<Grid.Row>
								<Grid.Column md={12}>
									<Field
										name="orderNo"
										type="text"
										id="orderNo"
										component="input"
										label={intl.formatMessage(messages.orderNoPlaceholder)}
										validation={[
											{
												rule: validationRules.isRequired,
												errorMessage: intl.formatMessage(messages.orderRequiredError),
											},
										]}
									/>
								</Grid.Column>
								<Grid.Column md={12}>
									<Field
										name="zip"
										type="text"
										component="input"
										id="zip"
										label={intl.formatMessage(messages.zipPlaceholder)}
										validation={[
											{
												rule: validationRules.isRequired,
												errorMessage: intl.formatMessage(messages.zipRequiredError),
											},
										]}
									/>
								</Grid.Column>
							</Grid.Row>
							{orderError && <span className={styles['track-order-form__error']}>{orderError}</span>}

							<Typography variant="p">{intl.formatMessage(messages.mandatoryFields)}</Typography>

							<div
								className={cn(
									styles['track-order-form__btn-wrapper'],
									styles['track-order-form__btn-wrapper--submit'],
								)}
							>
								<Button
									classNames={styles['track-order-form__btn']}
									disabled={submitting}
									loading={submitting}
									type="submit"
								>
									{intl.formatMessage(messages.submitButton)}
								</Button>
							</div>
						</VerifyForm>
					)}
				/>
			</Grid>
		</div>
	);
};

export default TrackOrderForm;
